/*
 * WM Set Unused Lots To Inactive Status (legacy WMD0068).
 *
 * Everything the screen says, and the small amount of vocabulary it has to agree with the API about.
 * Notify/confirm text lives in common/Messages.js, as it does on every sibling; the page's own bands,
 * captions and combo items live here, because they are reproductions of one Delphi form rather than
 * messages the app shares.
 */

// Parameter field names, shared by Layout.js (the schema) and the page (formRefs lookups and the
// request body). Declared once so a rename cannot silently break the page's imperative field access,
// which is keyed by these exact strings. They are also the request property names the API binds, so
// they match SetUnusedLotsInactiveDTO.
export const FIELD = Object.freeze({
    LastUpdateDate: "LastUpdateDate",
    UpdateLots: "UpdateLots",
    UpdateSublots: "UpdateSublots",
    UpdateUnits: "UpdateUnits",
    LotRecurringStorage: "LotRecurringStorage",
    SublotRecurringStorage: "SublotRecurringStorage",
});

/**
 * IMLOT/IMSUBLOT.RECURRINGSTORAGE as this screen writes it. Mirrors
 * Argos.Core/Constants/SetUnusedLotsInactiveConstants.cs -> RecurringStorageCodes.
 */
export const RecurringStorage = Object.freeze({
    AlwaysGenerate: "Y",
    GenerateUntilOnHandZero: "A",
    NeverGenerate: "N",
});

/**
 * "Do Nothing" — the combo's first item (ItemIndex 0 in Delphi), meaning "write no flag at all".
 *
 * It needs a non-empty key of its own rather than "". A DevExtreme SelectBox bound to an empty string
 * renders an empty box instead of the matching item's caption, so the user could not tell Do Nothing
 * from nothing-selected — on a screen where the difference is whether an UPDATE runs. Mapped back to
 * null by `toFlag` before the request is built; the API reads null/empty as Do Nothing.
 *
 * Deliberately not a code the server knows: if it ever reached the API it would be rejected by
 * RecurringStorageCodes.IsKnown rather than written to a column.
 */
export const DO_NOTHING = "-";

/** The wire value for a storage combo: the code, or null for Do Nothing. */
export const toFlag = (value) => (!value || value === DO_NOTHING ? null : value);

/**
 * The Last Update Date as "yyyy-MM-dd", or null when the box is empty.
 *
 * NOT the Date object straight off the DateBox, and not toISOString(). The field is a calendar date that
 * the server compares with `LASTUPDATEDATETIME <= @LastUpdateDate`, and a DateBox value is local
 * midnight — so east of UTC, toISOString() moves it to the previous evening and silently excludes a
 * day's worth of records. A bare date string binds to DateTime as that day at 00:00 with no zone
 * arithmetic at all, which is also exactly what the legacy's ab_DateToSQLStr produced.
 *
 * Built from the LOCAL getFullYear/getMonth/getDate for the same reason: the user picked the date they
 * see, and any UTC component would be the wrong day for half the world.
 */
export const toDateOnly = (value) => {
    if (!value) return null;

    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;

    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");

    return `${parsed.getFullYear()}-${month}-${day}`;
};

/**
 * Combo items verbatim from uWMD0068.dfm (edtLotLevelStorage / edtSublotLevelStorage share the same
 * four), in DFM order. Do Nothing is the default — FormAbLoadProgram sets both to ItemIndex 0.
 *
 * "Generate Until On Hand Reaches Zero" ('A') is not offered by the Item or Lot data-entry screens,
 * which load only Y and N. The code is real and this screen has written it since 2007, so it stays;
 * see the server-side constants for the same note.
 */
export const RECURRING_STORAGE_OPTIONS = [
    { key: DO_NOTHING, value: "Do Nothing" },
    { key: RecurringStorage.AlwaysGenerate, value: "Always Generate" },
    { key: RecurringStorage.GenerateUntilOnHandZero, value: "Generate Until On Hand Reaches Zero" },
    { key: RecurringStorage.NeverGenerate, value: "Never Generate" },
];

/**
 * The Record Range field catalogue. One pair, not one per level: uWMD0068.dfm binds
 * imSetStatusToInactive.DataSet to frmWMM0068.qryIMItem once and never repoints it, so the filter is
 * over IM Item Master whatever the checkboxes say.
 *
 * Must match Argos.Core/Constants/SetUnusedLotsInactiveConstants.cs -> SetUnusedLotsInactiveReports and
 * the ReportFactory case that resolves it.
 */
export const REPORT = Object.freeze({
    name: "WMSetUnusedLotsInactive",
    fullName: "Argos.Report.WM.WMSetUnusedLotsInactive, Argos.Report.WM",
});

/**
 * Saved-layout identity for the Preview grid.
 *
 * One key, unlike the purge screen's three: there is only ever one column set here, because there is
 * only one dataset behind the Preview.
 */
export const PREVIEW_GRID_KEY = "3F61C84A-27D9-4E05-9B73-5C8A1D60E492";

// GridLayoutManager's built-in layout name, used as the initial currentLayout state.
export const DEFAULT_LAYOUT_NAME = "Default";

// Dynamic-form layout identity for the parameters panel.
export const LAYOUT_ID = "B4D27E09-6A13-4F58-8C20-71E93A5B6D4F";
export const LAYOUT_NAME = "WMSetUnusedLotsInactiveForm";

// The form's lblTitle (uWMD0068.dfm). The window caption adds "(WMD0068)"; the title band is the one
// users read, so it is the one reproduced. Longer than most page titles because the screen genuinely
// does two things, and the menu leaf is the shorter "Set Unused Lots To Inactive Status".
export const PAGE_TITLE = "Set Unused Lots/Sublots To Inactive Status or Recurring Storage Flag";

// The read-only criteria memo (uWMD0068.dfm edtMemo), verbatim apart from collapsing the Delphi line
// wrapping and its leading indentation. It is the only place the screen explains which records qualify,
// so it is worth reproducing exactly rather than paraphrasing.
export const HELP_TEXT =
    "Warning : This Program Can Be Used To Set Lot, Sublot, Unit Records STATUS to INACTIVE Based on "
    + "Inventory Status Quantities.\n"
    + "It Can Also Be Used To Set Recurring Storage Flag For Lot and Sublot Records.\n"
    + "In Order To Change The STATUS to INACTIVE All Inventory Quantities Must Be Zero.\n"
    + "This Program Will Only Effect Records Where The Last Update Date is Less Than or Equal Date "
    + "Entered.";

// Group-box captions, verbatim from the DFM column labels (hdrBottomRight, hdrBottomLeft,
// hdrBottomRightStorage), in the form's left-to-right order.
export const GROUP_TITLE = Object.freeze({
    LastUpdateDate: "Update Through Last Update Date",
    Status: "Set Status To Inactive",
    RecurringStorage: "Set Recurring Storage Flag To:",
});

// Field captions, verbatim from the DFM.
export const FIELD_LABEL = Object.freeze({
    LastUpdateDate: "Last Update",
    UpdateLots: "Update Lot Records",
    UpdateSublots: "Update Sublot Records",
    UpdateUnits: "Update Unit Records",
    LotRecurringStorage: "Lot Level",
    SublotRecurringStorage: "Sublot Level",
});

/*
 * The Preview dialog's title.
 *
 * Says what the dialog lists, because this Preview is NOT the rows Start updates — btnPreviewClick runs
 * imSetStatusToInactive.Preview, i.e. the where-builder's own IM Item Master dataset. AI8 titled the
 * dialog plain "Preview" and left users to work that out; naming it is the one place this screen
 * deliberately reads better than the form it replaces.
 */
export const PREVIEW_TITLE = "Preview - IM Item Master Records Matching Your Criteria";

// Toolbar button captions, verbatim from the Delphi form (uWMD0068.dfm grpBottomButtons), in its
// left-to-right order. Help and Exit are not migrated: Help opened a Delphi context topic and Exit
// closed the MDI child.
export const BUTTON = Object.freeze({
    Start: "Start",
    Preview: "Preview",
    Reset: "Reset",
});

// The Delphi allowed its queries 900s on this family of screens, and the UPDATE batch is heavier still.
export const REQUEST_TIMEOUT_MS = 20 * 60 * 1000;

/**
 * Deadline for the GetReport that loads the filter catalogue.
 *
 * Needed rather than merely defensive: CommonReport.loadReport swallows a failure into console.log and
 * never calls onReportStateChange (CommonReport.js:555), so on a failed fetch nothing else would ever
 * lower the load panel. Short — this is a small metadata fetch, not the 20-minute update — but generous
 * enough that a slow-but-working server is not cut off mid-flight.
 */
export const REPORT_LOAD_TIMEOUT_MS = 30 * 1000;
